import axios from "axios";
import { toast } from "react-toastify";
import { navigateTo } from "../lib/navigationService";

const BASE_URL = `${process.env.REACT_APP_API_URL}/api`;

const pendingRequests = new Map();

const getRequestKey = (config) => {
  return `${config.method}-${config.url}-${JSON.stringify(
    config.params
  )}-${JSON.stringify(config.data)}`;
};

const cancelPreviousRequest = (config) => {
  const requestKey = getRequestKey(config);
  if (pendingRequests.has(requestKey)) {
    const controller = pendingRequests.get(requestKey);
    controller.abort();
    pendingRequests.delete(requestKey);
  }
};

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 8000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Cancel any previous duplicate request
  cancelPreviousRequest(config);

  // Create new AbortController for the current request
  const controller = new AbortController();
  config.signal = controller.signal;
  pendingRequests.set(getRequestKey(config), controller);

  return config;
});

// Add a response interceptor for centralized error handling
api.interceptors.response.use(
  (response) => {
    pendingRequests.delete(getRequestKey(response.config));
    return response;
  },
  (error) => {
    if (error.config) {
      pendingRequests.delete(getRequestKey(error.config));
    }

    // request cancelled
    if (axios.isCancel(error)) return;

    // timeout
    if (error.code === "ECONNABORTED") {
      if (!toast.isActive("serverError")) {
        toast("Server error. Please try again later.", {
          type: "error",
          toastId: "serverError",
        });
      }
      return Promise.reject(error);
    }

    const status = error.response?.status;

    // 🔥 TOKEN EXPIRED OR UNAUTHORIZED
    if (status === 401 || status === 403) {
      const hasToken = !!localStorage.getItem("token");

      if (hasToken) {
        localStorage.clear();

        if (!toast.isActive("sessionExpired")) {
          toast("Session expired. Please login again.", {
            type: "error",
            toastId: "sessionExpired",
          });
        }

        navigateTo("/login");

        // hard reload to reset all states
        setTimeout(() => {
          window.location.reload();
        }, 300);
      }

      return Promise.reject(error);
    }

    // account suspended
    if (status === 423) {
      localStorage.clear();

      if (!toast.isActive("forbidden")) {
        toast("Access denied. Your account has been suspended.", {
          type: "error",
          toastId: "forbidden",
        });
      }

      navigateTo("/");
      window.location.reload();

      return Promise.reject(error);
    }

    // network error
    if (!error.response) {
      if (!toast.isActive("networkError")) {
        toast("Network error, please check your connection.", {
          type: "error",
          toastId: "networkError",
        });
      }
    }

    return Promise.reject(error);
  }
);

export default api;
